import type { FileRouteTypes } from '@client/routeTree.gen';
import type { StaticDataRouteOption } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useGetRouteMatches } from './useGetRouteMatches';

/**
 * Merges the `staticData` of a chain of matched routes, parent → child.
 *
 * The merge is a SHALLOW spread: a child key replaces the parent's wholesale. A child that
 * declares `permissions` (or `modules`, `roles`, …) replaces the parent's list rather than
 * combining with it — access checks in `useHasRouteAccess` rely on this.
 */
export function mergeRouteStaticData(matches: readonly { staticData: StaticDataRouteOption }[]): StaticDataRouteOption {
    return matches.reduce<StaticDataRouteOption>((prev, item) => ({ ...prev, ...item.staticData }), {});
}

/**
 * Merges the `staticData` of every route matched by a path (see {@link mergeRouteStaticData}).
 *
 * Resolving a layout's path also matches its index route, so the result describes the page a
 * link to that path opens — not the layout on its own.
 */
export function useGetRouteMetadata() {
    const getRouteMatches = useGetRouteMatches();

    return useCallback((path: FileRouteTypes['to']) => mergeRouteStaticData(getRouteMatches(path)), [getRouteMatches]);
}
